import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Box,
} from "@material-ui/core";
import useStructureQueries from "hooks/useStructureQueries";
import MaskAndValid from "utils/maskAndValid";
import verifyProfilesVision from "utils/verifyProfilesVision";

import TableCnpj from "./components/tableCnpj";
import HeaderClientes from "components/_refactor/headers/clients";
import FilterCard from "components/_refactor/filter-card";
import InputText from "components/_refactor/text/input";
import StructureFields from "components/_refactor/fields/structure";
import AddressCard from "./components/address-dialog";
import RepresentativesCard from "./components/representate-dialog";
import SolicitarAlteracaoDialog from "./components/solicitar-alteracao-dialog";
import ContactCard from "./components/contact-dialog";
import UsersCard from "./components/users-dialog";

import CircleIcon from "@mui/icons-material/Circle";

export default function MeuCadastro() {
  const isRepresentant = verifyProfilesVision(["REPRESENTANTE", "ADM"]);

  const [structureFilters, setStructureFilters] = useState();
  const [clearFilters, setClearFilters] = useState(false);
  const [structureLoading, setStructureLoading] = useState([]);
  const [economicGroup, setEconomicGroup] = useState(null);
  const [searching, setSearching] = useState(false);
  const [openedModal, setOpenedModal] = useState(null);
  const [type, setType] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const isValidCnpj = cnpj ? MaskAndValid.validateCNPJ(cnpj) : false;
  const cnpjUnmasked = cnpj?.replace(/[^\d]+/g, "");

  const { getStoreGroupSearch } = useStructureQueries();

  const handleSearch = async () => {
    setSearching(true);
    try {
      const params = {};
      if (isRepresentant) {
        if (isValidCnpj) {
          params.cnpj = cnpjUnmasked;
        } else if (structureFilters) {
          params.idPessoaLoja = structureFilters?.idLoja;
        } else if (structureFilters?.idGrupoEconomico) {
          params.idGrupoEconomico = structureFilters.idGrupoEconomico;
        }
      }

      const result = await getStoreGroupSearch(params);
      setEconomicGroup(result?.data ?? null);
    } catch (error) {
      console.error("Erro ao buscar grupo econômico:", error);
      setEconomicGroup(null);
    } finally {
      setSearching(false);
    }
  };

  const handleClearFilters = () => {
    setEconomicGroup(null);
    setCnpj("");
    setStructureFilters(null);
    setClearFilters(true);

    setTimeout(() => {
      setClearFilters(false);
    }, 300);
  };

  const activeColor =
    economicGroup?.StatusGrupoEconomico === "ATIVO" ? "success" : "error";

  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <div>
      <HeaderClientes section="Meu Cadastro" subsection="Clientes" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        {isRepresentant && (
          <FilterCard
            handleSearch={handleSearch}
            disabled={
              !isValidCnpj &&
              !businessName &&
              !structureFilters?.idGrupoEconomico
            }
            searching={searching}
            handleClearFilters={handleClearFilters}
          >
            <StructureFields
              clearFilters={clearFilters}
              onStructureFieldsChange={setStructureFilters}
              disableAll={!!structureLoading.length}
              onStructureFieldsLoad={setStructureLoading}
              requiredFields={isValidCnpj ? [] : ["economicGroup"]}
              hideFields={["brand", "channel", "representative"]}
              handleSearch={handleSearch}
            ></StructureFields>
            <Grid item xs={12} md={6} lg={3}>
              <InputText
                label="CNPJ"
                value={cnpj}
                onChange={(e) => {
                  setCnpj(MaskAndValid.maskCNPJ(e.target.value));
                }}
                onInput={(e) => {
                  e.target.value = e.target.value.toString().slice(0, 18);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isValidCnpj) {
                    handleSearch();
                  }
                }}
                error={!!cnpj && !isValidCnpj}
                disabled={searching}
              />
            </Grid>
          </FilterCard>
        )}
        {economicGroup && (
          <>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">Código do Grupo</Typography>
                    <Typography variant="caption">
                      {economicGroup.IdGrupoEconomico}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">
                      Grupo Econômico/Razão Social
                    </Typography>
                    <Typography variant="caption">
                      {economicGroup.NomeGrupoEconomico}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Typography variant="subtitle2">Qtde Lojas</Typography>
                    <Box>
                      <Typography variant="caption" color="inherit">
                        {economicGroup.TotalLojasTotais} Totais
                      </Typography>{" "}
                      /{" "}
                      <Typography variant="caption" style={{ color: "green" }}>
                        {economicGroup.TotalLojasAtivas} Ativas
                      </Typography>{" "}
                      /{" "}
                      <Typography variant="caption" color="error">
                        {economicGroup.TotalLojasInativas} Inativas
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Typography variant="subtitle2">Código ERP</Typography>
                    <Typography variant="caption">
                      {economicGroup.CodigoErpGrupoEconomico}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={1} style={{ alignItems: "center" }}>
                    <Typography variant="subtitle2">Ativo</Typography>
                    <CircleIcon color={activeColor} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <TableCnpj
              data={economicGroup.Lojas}
              onClick={setOpenedModal}
              selectedRow={setSelectedRow}
            />
          </>
        )}
      </Container>

      {openedModal === "ADDRESS" && (
        <AddressCard
          storeId={selectedRow?.IdPessoaLoja}
          setOpenedModal={setOpenedModal}
          setType={setType}
        />
      )}

      {openedModal === "REPRESENTANTE" && (
        <RepresentativesCard
          storeId={selectedRow?.IdPessoaLoja}
          setOpenedModal={setOpenedModal}
          setType={setType}
        />
      )}

      {openedModal === "CONTACT" && (
        <ContactCard
          storeId={selectedRow?.IdPessoaLoja}
          setOpenedModal={setOpenedModal}
          setType={setType}
        />
      )}

      {openedModal === "USERS" && (
        <UsersCard
          storeId={selectedRow?.IdPessoaLoja}
          setOpenedModal={setOpenedModal}
          setType={setType}
        />
      )}

      {openedModal === "ALTERACAO" && (
        <SolicitarAlteracaoDialog
          storeId={selectedRow?.IdPessoaLoja}
          type={type}
          onClose={() => setOpenedModal(null)}
        />
      )}
    </div>
  );
}
